import React, { useState, useEffect } from "react";
import WorkspaceSelector from "../../Helper/WorkspaceSelector";
import Text from "../../Components/Text";
import WorkspaceCreation from "./WorkspaceCreation";
import UserActivation from "./UserActivation";
import IndexPage from "../Crud/IndexPage";
import { LoadingCircle } from "../../Components/LoadingCircle";
import { dbEvents } from "../../db/dbEvents";
import TIcon from "../../Components/TIcon";
import {getAllOrders} from "../../Model/Orders"
import PeerConnectionManager from "../../Services/PeerConnectionManager";
import { getUser } from "../../Helper/workspaceStorage";
import { getColor } from "../../Services/ColorService";
import { getWorkspace } from "../../Model/Workspace";
import SubscriptionHelper from "../../Helper/SubscriptionHelper";
import { useModal } from "../../Components/Modal";
import { WorkerLogin } from "./Counter/WorkerLogin";
import { findByUserCodeNoAccounted } from "../../Model/TimeTracking";
import { ClockIn } from "./ClockIn";

/**
 * Main Dashboard that contains the Register Button or Kitchen Terminal
 * Checks the subscription state of the WS or if the User is Activated or in a Workspace
 * @returns 
 */
const Dashboard = () => {
  const [user, setUser] = useState(null);
  const [currentTime, setCurrentTime] = useState(new Date());
  const { getCurrency } = WorkspaceSelector()
  const [activeOrders, setActiveOrders] = useState(0);
  const [inactiveOrders, setInactiveOrders] = useState(0);
  const [combinedOrders, setCombinedOrders] = useState([]);
  const {isAllowed, hasActive} = SubscriptionHelper()
  const {ModalView, openModal, closeModal} = useModal()

  const [workspace, setWorkspace] = useState(null)
  useEffect(()=>{
    async function saveWorkspace(){
      const data = await getWorkspace()
      setWorkspace(data)
    }
    saveWorkspace()
  },[])

  useEffect(() => {
    const fetchUserInfo = async () => {
      const userData = getUser()
      setUser(userData);
    };

    fetchUserInfo();
  }, []);

  // Update Seconds
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);

    return () => clearInterval(interval);
  }, []);

  // DB HOOK to refresh
  useEffect(() => {
    handleOrderData();

    const handleChange = (e) => {
      if (["orders", "sync_orders"].includes(e.detail.store)) {
        handleOrderData();
      }
    };
    dbEvents.addEventListener("dbChange", handleChange);

    return () => {
      dbEvents.removeEventListener("dbChange", handleChange);
    };
  }, []);

  const handleOrderData = async () => {
    const all = await getAllOrders();
  
    const active = all.filter(order => order.status === "PENDING");
    const inactive = all.filter(order => order.status === "COMPLETE");

    setActiveOrders(active.length);
    setInactiveOrders(inactive.length);
    const sortedOrders = [...active, ...inactive]
      .sort((a, b) => new Date(b.startTime) - new Date(a.startTime));
    setCombinedOrders(sortedOrders);
  };

  const getStatusLabel = (status) => {
        if (status == null) { return }
        if (status === "PENDING") { return "clock" }
        if (status === "COMPLETE") { return "check" }
        if (status === "CANCELED") { return "x" }
        if (status === "WASTE") { return "trash" }
    }

  const orderDashboardContent = () => (
    <div className="w-full h-[40vh] flex flex-col gap-3 pt-3 pl-3 pr-3 bg-white rounded-xl overflow-y-auto">
      <Text type="main">Letzte Bestellungen</Text>
      {combinedOrders.map((order, index) => (
        <div key={index} className="flex justify-between gap-1 p-2 border-b border-gray-300">
          <div className="flex gap-2">
            {order.status && <TIcon name={`${getStatusLabel(order.status)}`} size={20} />}
            <Text type="body">Bestellung: {order.id}</Text>
          </div>
          
          <div className="flex gap-2 flex-col text-right">
            <Text type="body">Preis: {order.totalPrice.toFixed(2)} {getCurrency()}</Text>
            <Text type="body">Start Time: {new Date(order.startTime).toLocaleString()}</Text>
          </div>
        </div>
      ))}
    </div>
  )

  async function getOrCreateNewTimeEntry(user){
    const timeTracking = await findByUserCodeNoAccounted(user) || {
      id: null,
      offlineId: `offline_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      user: user
    }
    openModal(<ClockIn obj={timeTracking} afterAction={() => closeModal()} />)
  }

  //When User in workspace
  const mainContent = () => (
    <div className="w-full h-full flex flex-col gap-y-4">
      <IndexPage
        headItems={[
          { 
            backgroundColor: "bg-[#FCFCFB]" ,
            customText: [
              { text: "Offene Bestellungen", type: "body", icon: "clock" },
              { text: `${activeOrders}`, type: "main" },
              { text: `${activeOrders > 0 ? 'Bestellungen noch offen' : 'Alles abgeschlossen'}`, type: "body", classNames: `${activeOrders > 0 ? 'text-red-500' : 'text-green-500'}` }
            ]
          },
          { 
            customText: [
              { text: "Uhrzeit", type: "body", icon: "device-watch" },
              { text: currentTime.toLocaleTimeString("de-CH", { hour: "2-digit", minute: "2-digit", second: "2-digit" }), type: "main" },
              { text: "Live", type: "body" }
            ],
            backgroundColor: "bg-[#FCFCFB]" 
          },
          { 
            customText: [
              { text: "Bestellungen Heute", type: "body", icon: "clipboard-text" },
              { text: `${activeOrders + inactiveOrders}`, type: "main" },
              { text: "seit Öffnung", type: "body" }
            ],
            backgroundColor: "bg-[#FCFCFB]" 
          }
        ]}
        items={[
          { text: "Kassenterminal", description: "Bestellung aufnehmen", icon: "cash-register", iconColor: getColor("primary"), iconBackground: `bg-[${getColor("secondary")}]`, path: "/kasse" },
          workspace?.kitchenMonitor && ({ text: "Küchenmonitor", description: "Offene Bons verfolgen", icon: "clipboard-list", iconColor: getColor("secondary"), iconBackground: `bg-[${getColor("primary")}]`, path: "/orders" }),
          (isAllowed("allowTimeTracking") && hasActive("useTimeTracking")) && (
            {text: "Stempeluhr", 
              description: "Ein und Ausstempeln", 
              icon: "clock", 
              iconColor: getColor("primary"), 
              iconBackground: `bg-[${getColor("secondary")}]`, 
              action: () => openModal(<WorkerLogin backButton={false} afteraction={(user) =>getOrCreateNewTimeEntry(user)} />),
              role: "MANAGER" }),
        ]}
        iconSize={50}
        buttonWrapping="flex-col md:flex-row"
        additionalContent={orderDashboardContent()}
      />
      <ModalView classNames="w-[600px] max-w-full max-h-full" />
    </div>
  );

  //When user is in no workspace
  const noWorkspaceContent = () => (
    <div className="w-full h-full bg-white p-3 flex justify-center">
      <div className="h-full flex flex-col gap-y-4 text-center">
        <Text type="maintitle">Du bist in noch keinem Workspace!</Text>
        <WorkspaceCreation />
      </div>
    </div>
  );

  //When user is not verified
  const notActiveContent = () => (
    <div className="w-full h-full bg-white flex justify-center">
      <div className="h-full flex flex-col gap-y-4 text-center">
        <UserActivation />
      </div>
    </div>
  );

  if (!user && !PeerConnectionManager.isConnected()) {
    return <LoadingCircle />
  }

  return PeerConnectionManager.isConnected() ? mainContent() : user.active ? (user.workspace.length > 0 ? mainContent() : noWorkspaceContent()) : notActiveContent();
};

export default Dashboard;
